import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Upload, Search, Package } from 'lucide-react';
import { imageFallback, resolveImageUrl } from '../../lib/imageUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name: '', description: '', category_id: '', subcategory_id: '', price: '', stock: '',
    discount_type: 'none', discount_value: '0', discount_active: true, discount_start: '', discount_end: '', featured: false
  });
  const [imageFiles, setImageFiles] = useState([]);

  useEffect(() => {
    fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [pRes, cRes, sRes] = await Promise.all([
        axios.get(`${BACKEND}/api/products`),
        axios.get(`${BACKEND}/api/categories`),
        axios.get(`${BACKEND}/api/subcategories`)
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
      setSubcategories(sRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({
      name: '', description: '', category_id: '', subcategory_id: '', price: '', stock: '',
      discount_type: 'none', discount_value: '0', discount_active: true, discount_start: '', discount_end: '', featured: false
    });
    setImageFiles([]);
    setEditing(null);
    setShowForm(false);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description, category_id: p.category_id,
      subcategory_id: p.subcategory_id || '',
      price: String(p.price), stock: String(p.stock),
      discount_type: p.discount_type || 'none', discount_value: String(p.discount_value || 0),
      discount_active: p.discount_active !== false,
      discount_start: p.discount_start ? p.discount_start.slice(0, 10) : '',
      discount_end: p.discount_end ? p.discount_end.slice(0, 10) : '',
      featured: p.featured
    });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('description', form.description);
    fd.append('category_id', form.category_id);
    fd.append('subcategory_id', form.subcategory_id);
    fd.append('price', form.price);
    fd.append('stock', form.stock);
    fd.append('discount_type', form.discount_type);
    fd.append('discount_value', form.discount_value);
    fd.append('discount_active', form.discount_active);
    fd.append('discount_start', form.discount_start);
    fd.append('discount_end', form.discount_end);
    fd.append('featured', form.featured);
    imageFiles.forEach(f => fd.append('images', f));

    try {
      if (editing) {
        await axios.put(`${BACKEND}/api/products/${editing}`, fd);
      } else {
        await axios.post(`${BACKEND}/api/products`, fd);
      }
      resetForm();
      fetchData();
    } catch (err) { alert('Error saving product'); console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${BACKEND}/api/products/${id}`);
    fetchData();
  };

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const availableSubcategories = subcategories.filter(s => s.category_id === form.category_id);

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-4xl font-light tracking-tighter text-white">Products</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="px-6 py-3 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-colors flex items-center space-x-2"
          data-testid="add-product-button">
          <Plus size={18} /><span>Add Product</span>
        </button>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
        <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full bg-[#141414] border border-white/10 text-white pl-10 pr-4 py-3 outline-none focus:border-[#D4AF37] transition-colors"
          data-testid="admin-search-products" />
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="product-form-modal">
          <div className="bg-[#141414] border border-white/10 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light text-white">{editing ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={resetForm} className="text-[#A3A3A3] hover:text-white" data-testid="close-product-form"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="product-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={3}
                  className="w-full bg-transparent border border-white/20 focus:border-[#D4AF37] text-white p-3 outline-none resize-none" data-testid="product-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Category</label>
                  <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value, subcategory_id: '' })} required
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-3 outline-none focus:border-[#D4AF37]" data-testid="product-category-select">
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Subcategory</label>
                  <select value={form.subcategory_id} onChange={e => setForm({ ...form, subcategory_id: e.target.value })}
                    disabled={!form.category_id}
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-3 outline-none focus:border-[#D4AF37] disabled:opacity-50" data-testid="product-subcategory-select">
                    <option value="">No Subcategory</option>
                    {availableSubcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Price (INR)</label>
                  <input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required min="0"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="product-price-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Stock</label>
                  <input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} required min="0"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="product-stock-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Discount Type</label>
                  <select value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-3 outline-none focus:border-[#D4AF37]" data-testid="product-discount-type-select">
                    <option value="none">None</option>
                    <option value="percentage">Percentage</option>
                    <option value="flat">Flat</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Discount Value</label>
                  <input type="number" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} min="0"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="product-discount-value-input" />
                </div>
                <div className="flex items-end space-x-3 pb-3">
                  <input type="checkbox" checked={form.discount_active} onChange={e => setForm({ ...form, discount_active: e.target.checked })}
                    className="w-4 h-4 accent-[#D4AF37]" data-testid="product-discount-active-checkbox" />
                  <label className="text-sm text-[#A3A3A3]">Offer Active</label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Offer Start</label>
                  <input type="date" value={form.discount_start} onChange={e => setForm({ ...form, discount_start: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#D4AF37] text-white px-3 py-3 outline-none" data-testid="product-discount-start-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Offer End</label>
                  <input type="date" value={form.discount_end} onChange={e => setForm({ ...form, discount_end: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#D4AF37] text-white px-3 py-3 outline-none" data-testid="product-discount-end-input" />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 accent-[#D4AF37]" data-testid="product-featured-checkbox" />
                <label className="text-sm text-[#A3A3A3]">Featured Product</label>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Images</label>
                <label className="flex items-center justify-center border-2 border-dashed border-white/20 hover:border-[#D4AF37] p-6 cursor-pointer transition-colors" data-testid="product-images-upload">
                  <div className="text-center space-y-2">
                    <Upload size={24} className="mx-auto text-[#A3A3A3]" />
                    <p className="text-sm text-[#A3A3A3]">{imageFiles.length > 0 ? `${imageFiles.length} file(s) selected` : 'Click to upload images'}</p>
                  </div>
                  <input type="file" multiple accept="image/*" className="hidden" onChange={e => setImageFiles(Array.from(e.target.files))} />
                </label>
              </div>
              <button type="submit"
                className="w-full py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-colors"
                data-testid="save-product-button">
                {editing ? 'Update Product' : 'Create Product'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? <p className="text-[#A3A3A3]">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Product</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Category</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Subcategory</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Price</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Discount</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Stock</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Featured</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`product-row-${p.id}`}>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      {p.images?.[0] ? (
                        <img src={resolveImageUrl(p.images[0], BACKEND)} alt="" onError={imageFallback} className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center"><Package size={16} className="text-[#A3A3A3]" /></div>
                      )}
                      <span className="text-white">{p.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-[#A3A3A3]">{p.category_name}</td>
                  <td className="py-4 px-4 text-[#A3A3A3]">{p.subcategory_name || '-'}</td>
                  <td className="py-4 px-4">
                    <span className="text-[#D4AF37]">&#8377;{p.final_price.toLocaleString()}</span>
                    {(p.offer_applied || p.final_price < p.price) && <span className="text-[#A3A3A3] line-through text-xs ml-2">&#8377;{p.price.toLocaleString()}</span>}
                  </td>
                  <td className="py-4 px-4 text-[#A3A3A3]">
                    {p.discount_type === 'percentage' ? `${p.discount_value}%` : p.discount_type === 'flat' ? `\u20b9${p.discount_value}` : '-'}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`${p.stock === 0 ? 'text-[#FF3B30]' : p.stock < 5 ? 'text-[#FF9500]' : 'text-[#34C759]'}`}>{p.stock}</span>
                  </td>
                  <td className="py-4 px-4">
                    {p.featured ? <span className="text-[#D4AF37] text-xs uppercase">Yes</span> : <span className="text-[#A3A3A3] text-xs">No</span>}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <button onClick={() => openEdit(p)} className="text-[#A3A3A3] hover:text-white transition-colors" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                      <button onClick={() => handleDelete(p.id)} className="text-[#A3A3A3] hover:text-[#FF3B30] transition-colors" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-[#A3A3A3]">No products found</p>}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
